/**
 * AI 規定コマンド利用状況マトリクスの行内訳モーダル。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business マトリクス行 (人 / PJ) の規定コマンド内訳を工程 × 論理コマンド粒度で表示するモーダル。PJ 行では Backlog ステータス・開発担当者・コマンド利用ユーザー一覧も表示する
 * @graph-connects none
 */

import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import type {
  CcCommandMatrixCommandCount,
  CcCommandMatrixMemberRow,
  CcCommandMatrixProjectRow,
} from '../../../types/api.js';

/** Backlog チケットの閲覧 URL を作る。@graph-connects none */
export function backlogTicketUrl(ticketKey: string): string {
  return `https://air-closet.backlog.jp/view/${ticketKey}`;
}

/** モーダルに出す行の選択状態。null で閉じる。@graph-connects none */
export type CcCommandMatrixDetailSelection =
  | { kind: 'member'; row: CcCommandMatrixMemberRow }
  | { kind: 'project'; row: CcCommandMatrixProjectRow };

export interface CcCommandMatrixDetailModalProps {
  /** マスタの工程一覧。内訳の表示順に使う */
  phases: string[];
  /** 表示対象の行。null なら非表示 */
  selection: CcCommandMatrixDetailSelection | null;
  /** 閉じる要求 */
  onClose: () => void;
}

/**
 * commandCounts を工程 → 実行数降順で安定に並べる。
 * 工程順はマスタの phases 順を正とし、未知の工程は末尾に置く。
 * @graph-connects none
 */
export function sortCommandCounts(
  commandCounts: CcCommandMatrixCommandCount[],
  phases: string[],
): CcCommandMatrixCommandCount[] {
  const phaseOrder = new Map(phases.map((phase, index) => [phase, index]));
  const orderOf = (phase: string): number => phaseOrder.get(phase) ?? phases.length;
  return [...commandCounts].sort(
    (a, b) =>
      orderOf(a.phase) - orderOf(b.phase) ||
      b.count - a.count ||
      a.command.localeCompare(b.command),
  );
}

/** @graph-connects none */
function CommandBreakdownTable({
  commandCounts,
  phases,
}: {
  commandCounts: CcCommandMatrixCommandCount[];
  phases: string[];
}) {
  const { t } = useTranslation();
  if (commandCounts.length === 0) {
    return (
      <p className="py-2 text-sm text-[var(--color-text-muted)]">
        {t('ccCommandMatrix.detail.noCommands')}
      </p>
    );
  }
  const sorted = sortCommandCounts(commandCounts, phases);
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
          <th className="py-1.5 pr-4">{t('ccCommandMatrix.detail.phase')}</th>
          <th className="py-1.5 pr-4">{t('ccCommandMatrix.detail.command')}</th>
          <th className="py-1.5 text-right">{t('ccCommandMatrix.detail.count')}</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((item) => (
          <tr
            key={`${item.phase}:${item.command}`}
            className="border-b border-[var(--color-border)]"
          >
            <td className="py-1.5 pr-4 text-[var(--color-text-secondary)] whitespace-nowrap">
              {item.phase}
            </td>
            <td className="py-1.5 pr-4 text-[var(--color-text-primary)]">{item.command}</td>
            <td className="py-1.5 text-right tabular-nums text-[var(--color-text-primary)]">
              {item.count.toLocaleString()}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** @graph-connects none */
function SectionHeading({ children }: { children: string }) {
  return (
    <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
      {children}
    </h3>
  );
}

/** @graph-connects none */
function ProjectMeta({ row }: { row: CcCommandMatrixProjectRow }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
      {row.ticketStatus ? <Badge variant="default">{row.ticketStatus}</Badge> : null}
      <span className="text-[var(--color-text-secondary)]">
        {t('ccCommandMatrix.detail.assignee')}: {row.assignee ?? t('ccCommandMatrix.unassigned')}
      </span>
      <span className="text-[var(--color-text-secondary)]">
        {t('ccCommandMatrix.detail.mainWorker')}:{' '}
        {row.mainWorker ?? t('ccCommandMatrix.unassigned')}
      </span>
      {row.ticketKey ? (
        <a
          href={backlogTicketUrl(row.ticketKey)}
          target="_blank"
          rel="noreferrer"
          className="underline text-[var(--color-accent-primary)]"
        >
          {t('ccCommandMatrix.detail.backlogLink')}
        </a>
      ) : null}
    </div>
  );
}

/** @graph-connects none */
function ProjectCommandUsers({ row }: { row: CcCommandMatrixProjectRow }) {
  const { t } = useTranslation();
  if (row.commandUsers.length === 0) {
    return (
      <p className="py-2 text-sm text-[var(--color-text-muted)]">
        {t('ccCommandMatrix.detail.noCommandUsers')}
      </p>
    );
  }
  return (
    <ul className="space-y-1 text-sm">
      {row.commandUsers.map((user) => (
        <li key={user.email} className="flex items-center justify-between gap-4">
          <span className="text-[var(--color-text-primary)]">
            {user.nickname ?? t('ccCommandMatrix.unassigned')}
            <span className="text-xs text-[var(--color-text-muted)] ml-2">({user.email})</span>
          </span>
          <span className="tabular-nums text-[var(--color-text-secondary)]">
            {user.count.toLocaleString()}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-command-matrix 行内訳モーダル (presentation のみ)
 * @graph-connects Modal [calls] 内訳のオーバーレイ表示
 */
export function CcCommandMatrixDetailModal({
  phases,
  selection,
  onClose,
}: CcCommandMatrixDetailModalProps) {
  const { t } = useTranslation();
  if (selection === null) {
    return null;
  }
  const title =
    selection.kind === 'member'
      ? (selection.row.nickname ?? selection.row.email)
      : (selection.row.ticketKey ?? t('ccCommandMatrix.unlinkedProject'));
  const description =
    selection.kind === 'member' ? selection.row.email : (selection.row.ticketSummary ?? undefined);
  return (
    <Modal open onClose={onClose} title={title} description={description} size="lg">
      <div className="space-y-4">
        {selection.kind === 'project' ? <ProjectMeta row={selection.row} /> : null}
        <div className="space-y-2">
          <SectionHeading>{t('ccCommandMatrix.detail.commandBreakdown')}</SectionHeading>
          <CommandBreakdownTable commandCounts={selection.row.commandCounts} phases={phases} />
        </div>
        {selection.kind === 'project' ? (
          <div className="space-y-2">
            <SectionHeading>{t('ccCommandMatrix.detail.commandUsers')}</SectionHeading>
            <ProjectCommandUsers row={selection.row} />
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
